import React from 'react'
import { Box, List, ListItem, Typography, useTheme } from '@mui/material'
import { formatCamelCase } from 'components/DataGrid/operators/formatCamelCase'

export interface ValidationTooltipContentProps {
  /** Title shown at the top of the tooltip */
  title: string
  /** Object of validation errors where key is field name and value is boolean (true = error) */
  validationErrors?: Record<string, boolean>
  /** Whether save is required (adds "Save Required" to the list) */
  saveRequired?: boolean
  /** Whether all validations pass (shows success message) */
  isValid?: boolean
  /** Message to show when valid */
  successMessage?: string
  /** Message to show when completed */
  completedMessage?: string
  /** Whether the action is completed */
  isCompleted?: boolean
  /** Custom content to show instead of the default validation list */
  customContent?: React.ReactNode
}

const ValidationTooltipContent: React.FC<ValidationTooltipContentProps> = ({
  title,
  validationErrors = {},
  saveRequired = false,
  isValid = false,
  successMessage,
  completedMessage,
  isCompleted = false,
  customContent,
}) => {
  const theme = useTheme()

  // If completed, show completed message
  if (isCompleted && completedMessage) {
    return <Typography sx={{ color: 'text.success' }}>{completedMessage}</Typography>
  }

  // If valid and has success message, show it
  if (isValid && successMessage) {
    return <Typography sx={{ color: 'text.success' }}>{successMessage}</Typography>
  }

  // If custom content is provided, use it
  if (customContent) {
    return <>{customContent}</>
  }

  // Get the list of errors
  const errorEntries = Object.entries(validationErrors).filter(([, value]) => value === true)
  const hasErrors = errorEntries.length > 0 || saveRequired

  // If no errors and valid, just show the title
  if (!hasErrors && isValid) {
    return <Typography>{title}</Typography>
  }

  // Show validation error list
  return (
    <Box sx={{ textAlign: 'left' }}>
      <Typography sx={{ fontSize: 16, color: 'text.primary', mb: 1 }}>{title}</Typography>
      {hasErrors && (
        <>
          <Typography sx={{ color: 'text.secondary' }}>Required:</Typography>
          <List>
            {errorEntries.map(([key]) => (
              <ListItem key={key}>
                <Typography sx={{ color: theme.palette.text.error }}>{formatCamelCase(key)}</Typography>
              </ListItem>
            ))}
            {saveRequired && (
              <ListItem>
                <Typography sx={{ color: theme.palette.text.error }}>Save Required</Typography>
              </ListItem>
            )}
          </List>
        </>
      )}
    </Box>
  )
}

export default ValidationTooltipContent
